import { useMemo, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@agms/ui'
import { ticketTypeApi, type TicketType } from '../lib/api'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage } from '../hooks/useErrorHandler'
import { CardTitle as SectionCardTitle, PageHeader, SurfaceCard } from '../components/ui'

const CATEGORY_LABELS: Record<string, string> = {
  billing: 'Billing',
  technical: 'Technical',
  onboarding: 'Onboarding',
  general: 'General',
}

function groupLabel(group: string | null): string {
  return group?.trim() || 'Ungrouped'
}

function statusClass(ticketType: TicketType): string {
  if (ticketType.archived_at) return 'bg-gray-100 text-gray-600'
  return ticketType.active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
}

function statusLabel(ticketType: TicketType): string {
  if (ticketType.archived_at) return 'Archived'
  return ticketType.active ? 'Active' : 'Inactive'
}

function daysLabel(ticketType: TicketType): string {
  switch (ticketType.default_sla_type) {
    case '1_day':
      return '1 day'
    case '2_3_days':
      return '3 days'
    case '7_days':
      return '7 days'
    case 'beginning_of_next_month':
      return 'Beginning of next month'
    case 'custom':
      return `${ticketType.default_sla_custom_days ?? '?'} days`
    default:
      return '-'
  }
}

export function SettingsTicketTypesPage() {
  const queryClient = useQueryClient()
  const { canManageTicketTypes } = usePermissions()
  const [includeArchived, setIncludeArchived] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: ticketTypes, isLoading } = useQuery({
    queryKey: ['settings-ticket-types', includeArchived],
    queryFn: () => ticketTypeApi.list({ include_archived: includeArchived }),
  })

  const archiveMutation = useMutation({
    mutationFn: (ticketType: TicketType) => ticketType.archived_at
      ? ticketTypeApi.restore(ticketType.id)
      : ticketTypeApi.archive(ticketType.id),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['settings-ticket-types'] })
      await queryClient.invalidateQueries({ queryKey: ['ticket-types'] })
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not update ticket type.'))
    },
  })

  const grouped = useMemo(() => {
    const groups = new Map<string, TicketType[]>()
    ;(ticketTypes ?? []).forEach((ticketType) => {
      const key = groupLabel(ticketType.type_group)
      groups.set(key, [...(groups.get(key) ?? []), ticketType])
    })
    return Array.from(groups.entries())
  }, [ticketTypes])

  if (!canManageTicketTypes()) {
    return <Navigate to="/settings" replace />
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ticket Types"
        subtitle="Reusable ticket templates with defaults and internal checklists."
        action={(
          <Link to="/settings/ticket-types/new">
            <Button>+ New Ticket Type</Button>
          </Link>
        )}
      />

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <SurfaceCard padding="p-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Show archived ticket types
        </label>
      </SurfaceCard>

      <SurfaceCard>
        {isLoading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading ticket types...</p>
        ) : grouped.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No ticket types found.</p>
        ) : (
          <div className="space-y-6">
            {grouped.map(([group, items]) => (
              <div key={group}>
                <SectionCardTitle>{group}</SectionCardTitle>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Category</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Usage</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Default SLA</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {items.map((ticketType) => (
                        <tr key={ticketType.id}>
                          <td className="px-3 py-2">
                            <Link to={`/settings/ticket-types/${ticketType.id}`} className="font-semibold text-blue-700 hover:text-blue-900">
                              {ticketType.name}
                            </Link>
                            <div className="text-xs text-gray-500">{ticketType.checklist_items?.length ?? 0} checklist item(s)</div>
                          </td>
                          <td className="px-3 py-2 text-gray-700">{CATEGORY_LABELS[ticketType.default_category]}</td>
                          <td className="px-3 py-2 text-gray-700">{ticketType.usage_count ?? 0}</td>
                          <td className="px-3 py-2 text-gray-700">{daysLabel(ticketType)}</td>
                          <td className="px-3 py-2">
                            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusClass(ticketType)}`}>
                              {statusLabel(ticketType)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-2">
                              <Link to={`/settings/ticket-types/${ticketType.id}`}>
                                <Button variant="secondary">Edit</Button>
                              </Link>
                              <Button
                                variant="secondary"
                                onClick={() => archiveMutation.mutate(ticketType)}
                                disabled={archiveMutation.isPending}
                              >
                                {ticketType.archived_at ? 'Restore' : 'Archive'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </SurfaceCard>
    </div>
  )
}
